import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { useSnapshotStore } from "@/stores/snapshotStore";

interface RestoreDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  snapshot: {
    id: string;
    name: string;
    description: string | null;
    created_at: string;
    record_counts: Record<string, number>;
  } | null;
}

export function RestoreDialog({ open, onOpenChange, snapshot }: RestoreDialogProps) {
  const { restoreSnapshot, isLoading } = useSnapshotStore();

  if (!snapshot) return null;

  const handleRestore = async () => {
    try {
      await restoreSnapshot(snapshot.id);
      onOpenChange(false);
    } catch (error) {
      // Error is handled in the store
    }
  };

  const totalRecords = Object.values(snapshot.record_counts).reduce((sum, count) => sum + count, 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Restore Checkpoint</DialogTitle>
          <DialogDescription>
            Restore your data to: <strong>{snapshot.name}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <Alert variant="danger">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Warning:</strong> This will replace all your current data with the checkpoint data.
              A backup of your current state will be created automatically before restoring.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <div className="text-sm text-muted-foreground">
              <strong>Checkpoint Date:</strong> {new Date(snapshot.created_at).toLocaleString()}
            </div>
            {snapshot.description && (
              <div className="text-sm text-muted-foreground">
                <strong>Description:</strong> {snapshot.description}
              </div>
            )}
            <div className="text-sm text-muted-foreground">
              <strong>Total Records:</strong> {totalRecords}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs">
            {Object.entries(snapshot.record_counts)
              .filter(([_, count]) => count > 0)
              .map(([table, count]) => (
                <div key={table} className="flex justify-between p-2 bg-muted rounded">
                  <span className="capitalize">{table.replace('_', ' ')}:</span>
                  <span className="font-medium">{count}</span>
                </div>
              ))}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={handleRestore}
            disabled={isLoading}
          >
            {isLoading ? 'Restoring...' : 'Restore Checkpoint'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
